import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Play, Pause, Plus, Trash2, Eye, Send, Users, Save,
  AlertTriangle, Clock, Archive, Type, Code,
} from 'lucide-react';
import {
  apiGetCampaign, apiUpdateCampaign, apiStartCampaign, apiPauseCampaign,
  apiEnrollContacts, apiPreviewCampaign, apiTestCampaign, apiCampaignActivity,
  apiContactLists, apiArchiveCampaign, apiCampaignAudience, apiDisqualifyCampaignContacts,
} from '../../api';
import {
  Stat, StatusBadge, Field, Toggle, Empty, ActionButton, Toast, useToast,
  formatDate, formatDateTime, GATE_REASONS,
} from './parts';
import HtmlBodyEditor from './HtmlBodyEditor';
import SendingFrom from './SendingFrom';
import ModalShell from '../Modal/ModalShell';
import styles from './Mailer.module.css';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Berlin', 'Asia/Karachi', 'Asia/Dubai', 'Asia/Kolkata',
  'Australia/Sydney', 'UTC',
];

/**
 * One campaign: its sequence, its throttling, who is in it, and why it is or is
 * not sending right now.
 */
// Mailbox providers that will not authenticate mail relayed through Zoho.
// Zoho can "send as" these, but the message still leaves Zoho's servers, and
// these domains authorise neither Zoho's IPs in SPF nor a DKIM key for them.
const FREE_MAIL = ['gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'live.com', 'aol.com'];

const CampaignEditor = ({ jwtToken, campaignId, mailboxes = [], onChanged, onArchived }) => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const report = useToast();

  const [lists, setLists] = useState([]);
  const [enrollList, setEnrollList] = useState('');
  const [excludeRoles, setExcludeRoles] = useState(true);
  const [audience, setAudience] = useState({ summary: {}, contacts: [] });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactListOpen, setContactListOpen] = useState(false);
  const [contactListMode, setContactListMode] = useState('all');
  const [contactList, setContactList] = useState({ contacts: [], page: 1, pages: 1, total: 0 });
  const [contactListPage, setContactListPage] = useState(1);
  const [contactSearchInput, setContactSearchInput] = useState('');
  const [contactSearch, setContactSearch] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewStep, setPreviewStep] = useState(0);
  const [testTo, setTestTo] = useState('');
  // On a pooled campaign each domain has to be tested separately: SPF, DKIM and
  // inbox placement are per-domain, so one passing test says nothing about the
  // other two.
  const [testMailbox, setTestMailbox] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  // The preview renders below the whole sequence, so on a long campaign it
  // opens off-screen and the button looks dead. Scroll it into view.
  const previewRef = useRef(null);
  const [activity, setActivity] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [detail, listData, activityData] = await Promise.all([
        apiGetCampaign(jwtToken, campaignId),
        apiContactLists(jwtToken),
        apiCampaignActivity(jwtToken, campaignId, { limit: 25 }),
      ]);
      setData(detail);
      setDraft(JSON.parse(JSON.stringify(detail.campaign)));
      setLists(listData.lists || []);
      setActivity(activityData.messages || []);
    } catch (e) {
      setError(e.message || 'Could not load the campaign');
    } finally {
      setLoading(false);
    }
  }, [jwtToken, campaignId]);

  useEffect(() => { load(); }, [load]);

  const loadAudience = useCallback(async () => {
    const result = await apiCampaignAudience(jwtToken, campaignId, {
      list: enrollList,
      mode: 'replied',
      page: 1,
      limit: 1,
    });
    setAudience(result || { summary: {}, contacts: [] });
  }, [jwtToken, campaignId, enrollList]);

  useEffect(() => {
    loadAudience().catch((e) => setError(e.message || 'Could not load campaign audience'));
  }, [loadAudience]);

  useEffect(() => {
    setContactListPage(1);
    setSelectedContacts([]);
  }, [enrollList]);

  const loadContactList = useCallback(async () => {
    if (!contactListOpen) return;
    const result = await apiCampaignAudience(jwtToken, campaignId, {
      list: enrollList,
      mode: contactListMode,
      search: contactSearch,
      page: contactListPage,
      limit: 50,
    });
    setContactList(result || { contacts: [], page: 1, pages: 1, total: 0 });
  }, [jwtToken, campaignId, enrollList, contactListOpen, contactListMode, contactSearch, contactListPage]);

  useEffect(() => {
    loadContactList().catch((e) => setError(e.message || 'Could not load contacts'));
  }, [loadContactList]);

  const save = async () => {
    try {
      await apiUpdateCampaign(jwtToken, campaignId, {
        name: draft.name,
        fromAddress: draft.fromAddress,
        fromName: draft.fromName,
        replyToAddress: draft.replyToAddress,
        steps: draft.steps,
        schedule: draft.schedule,
        rampUp: draft.rampUp,
        stopOnReply: draft.stopOnReply,
        includeUnsubscribe: draft.includeUnsubscribe,
      });
      report.ok('Campaign saved');
      await load();
      onChanged?.();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const toggleRunning = async () => {
    try {
      if (draft.status === 'running') {
        await apiPauseCampaign(jwtToken, campaignId);
        report.ok('Campaign paused — nothing further will send');
      } else {
        await apiStartCampaign(jwtToken, campaignId);
        report.ok('Campaign started. The first email goes out on the next tick, within a couple of minutes.');
      }
      await load();
      onChanged?.();
    } catch (e) {
      // The preflight checklist comes back as `problems`; show it rather than
      // the generic headline, since it says exactly what to fix.
      report.fail(e.problems?.length ? new Error(e.problems.join(' ')) : e);
      throw e;
    }
  };

  const enroll = async () => {
    try {
      const result = await apiEnrollContacts(jwtToken, campaignId, {
        filter: { list: enrollList || undefined, excludeRoleAccounts: excludeRoles },
      });
      report.ok(`Enrolled ${result.enrolled} contacts${result.skipped ? ` — ${result.skipped} were already in this campaign` : ''}`);
      await load();
      await loadAudience();
      onChanged?.();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const searchContactList = (event) => {
    event.preventDefault();
    setContactListPage(1);
    setContactSearch(contactSearchInput.trim());
  };

  const openContactList = (mode) => {
    setContactListMode(mode);
    setContactListPage(1);
    setContactSearchInput('');
    setContactSearch('');
    setSelectedContacts([]);
    setContactListOpen(true);
  };

  const toggleAudienceContact = (contactId) => {
    setSelectedContacts((current) => current.includes(contactId)
      ? current.filter((id) => id !== contactId)
      : [...current, contactId]);
  };

  const disqualifySelected = async () => {
    if (!selectedContacts.length) return;
    if (!window.confirm(`Disqualify ${selectedContacts.length} selected contact${selectedContacts.length === 1 ? '' : 's'} from this campaign?`)) return;
    try {
      const result = await apiDisqualifyCampaignContacts(jwtToken, campaignId, selectedContacts);
      report.ok(`${result.disqualified} contact${result.disqualified === 1 ? '' : 's'} disqualified`);
      await Promise.all([load(), loadAudience(), loadContactList()]);
      setSelectedContacts([]);
      onChanged?.();
    } catch (e) {
      report.fail(e);
    }
  };

  const runPreview = async (stepIndex) => {
    setPreviewStep(stepIndex);
    setPreviewLoading(true);
    try {
      setPreview(await apiPreviewCampaign(jwtToken, campaignId, {
        stepIndex,
        mailboxId: testMailbox || undefined,
      }));
      requestAnimationFrame(() => {
        previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (e) {
      report.fail(e);
      throw e;
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTest = async () => {
    try {
      const result = await apiTestCampaign(jwtToken, campaignId, {
        to: testTo || undefined,
        stepIndex: previewStep,
        mailboxId: testMailbox || undefined,
      });
      report.ok(`Test sent from ${result.from} to ${result.to}. Check which folder it landed in, including spam.`);
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const archive = async () => {
    if (!window.confirm(`Archive "${draft.name}"?

Sending stops and remaining contacts are released. The history is kept.`)) return;
    try {
      await apiArchiveCampaign(jwtToken, campaignId);
      onArchived?.();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  const patchStep = (index, changes) => {
    setDraft((d) => ({
      ...d,
      steps: d.steps.map((s, i) => (i === index ? { ...s, ...changes } : s)),
    }));
  };

  const addStep = () => {
    setDraft((d) => ({
      ...d,
      steps: [...d.steps, { waitDays: 4, subject: '', body: '', active: true }],
    }));
  };

  const removeStep = (index) => {
    setDraft((d) => ({ ...d, steps: d.steps.filter((_, i) => i !== index) }));
  };

  const patchSchedule = (changes) => {
    setDraft((d) => ({ ...d, schedule: { ...d.schedule, ...changes } }));
  };

  const toggleDay = (day) => {
    const days = draft.schedule.daysOfWeek || [];
    patchSchedule({
      daysOfWeek: days.includes(day) ? days.filter((x) => x !== day) : [...days, day].sort(),
    });
  };

  if (loading) return <div className="empty"><span className="spinner" style={{ width: 30, height: 30 }} /></div>;
  if (!draft) return <div className={styles.dangerBox}>{error || 'Campaign not found'}</div>;

  const running = draft.status === 'running';
  const {
    gate, forecast, remaining, enrollmentCounts = {}, problems = [],
    allocations = [], perDay = 0, perDayToday = 0, capacity,
  } = data;

  // Zoho will "send as" a Gmail or Outlook address, but the message still
  // leaves Zoho's servers and those domains authorise neither Zoho's IPs in SPF
  // nor a DKIM key for them — so it arrives unauthenticated and gets filtered.
  const freeMailInUse = allocations
    .map((a) => a.domain)
    .filter((d) => FREE_MAIL.includes(String(d).toLowerCase()));

  return (
    <div>
      <Toast message={report.toast?.message} tone={report.toast?.tone} onDismiss={report.clear} />

      <div className="page-header" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            className="input"
            style={{ fontSize: 20, fontWeight: 700, border: 'none', padding: '4px 0', background: 'none' }}
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <StatusBadge status={draft.status} />
            <span className="text-sm text-muted">
              {remaining} of {draft.stats?.enrolled || 0} contacts left
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton
            className="btn btn-ghost"
            onClick={save}
            icon={<Save size={16} />}
            busyLabel="Saving…"
            doneLabel="Saved"
            failLabel="Not saved"
          >
            Save
          </ActionButton>
          <ActionButton
            className={running ? 'btn btn-ghost' : 'btn btn-primary'}
            onClick={toggleRunning}
            icon={running ? <Pause size={16} /> : <Play size={16} />}
            busyLabel={running ? 'Pausing…' : 'Starting…'}
            doneLabel={running ? 'Paused' : 'Started'}
            failLabel={running ? 'Not paused' : 'Cannot start'}
          >
            {running ? 'Pause' : 'Start'}
          </ActionButton>
        </div>
      </div>

      {error && <div className={styles.dangerBox}>{error}</div>}
      {/* Everything blocking launch, as one checklist. */}
      {problems.length > 0 && !running && (
        <div className={styles.warnBox}>
          <div className={styles.boxTitle}><AlertTriangle size={16} /> Before this can start</div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}

      {/* Why a running campaign is idle right now. */}
      {running && gate && !gate.canSend && (
        <div className={styles.infoBox}>
          <div className={styles.boxTitle}><Clock size={16} /> {GATE_REASONS[gate.reason] || gate.reason}</div>
          Sent {gate.sentToday} of {gate.currentCap} allowed today. Local time in the campaign’s
          timezone is {String(gate.localTime?.hour).padStart(2, '0')}:{String(gate.localTime?.minute).padStart(2, '0')}.
        </div>
      )}

      <div className={styles.statGrid}>
        <Stat value={draft.stats?.sent || 0} label="Emails sent" />
        <Stat value={draft.stats?.replied || 0} label="Replies" tone="success" />
        <Stat value={enrollmentCounts.active || 0} label="In sequence" />
        <Stat value={draft.stats?.bounced || 0} label="Bounced" tone={draft.stats?.bounced ? 'danger' : undefined} />
        <Stat
          value={perDayToday === perDay ? perDay : `${perDayToday} of ${perDay}`}
          label={allocations.length > 1 ? `A day, across ${allocations.length} domains` : 'Emails a day'}
        />
      </div>

      {remaining > 0 && forecast?.finishesOn && (
        <div className={styles.infoBox}>
          <div className={styles.boxTitle}><Clock size={16} /> Projected finish</div>
          {remaining} contacts remaining at up to {forecast.perDay}/day works out to roughly{' '}
          <strong>{forecast.days} days</strong>, finishing around <strong>{formatDate(forecast.finishesOn)}</strong>.
          Follow-ups share the same daily budget. Cold outreach is meant to be slow — raising the cap
          to hurry it along is the quickest way to get filtered.
        </div>
      )}

      {/* ── Sending from ────────────────────────────────────────────────── */}
      {freeMailInUse.length > 0 && (
        <div className={styles.dangerBox}>
          <div className={styles.boxTitle}>
            <AlertTriangle size={16} /> {freeMailInUse.join(', ')} cannot authenticate through Zoho
          </div>
          Mail from these addresses still leaves Zoho’s servers, but those domains authorise neither
          Zoho’s IPs in SPF nor a DKIM key for them. SPF and DKIM both fail, so DMARC alignment
          fails — the message arrives unauthenticated and gets filtered. Send only from domains you
          control.
        </div>
      )}

      <SendingFrom
        jwtToken={jwtToken}
        campaignId={campaignId}
        allocations={allocations}
        mailboxes={capacity?.mailboxes || mailboxes}
        sendMode={draft.sendMode}
        report={report}
        onSaved={async () => { await load(); onChanged?.(); }}
      />

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="card-title" style={{ marginBottom: 4 }}>Sender name</h3>
        <p className="page-subtitle" style={{ marginBottom: 16 }}>
          Each mailbox carries its own name and signature — set those on the Mailboxes tab, so a
          second domain can be a genuinely different persona. This overrides them for this campaign
          only.
        </p>
        <div className={styles.fieldRow}>
          <Field label="From name" help="Leave blank to use each mailbox’s own name.">
            <input
              className="input"
              value={draft.fromName || ''}
              onChange={(e) => setDraft((d) => ({ ...d, fromName: e.target.value }))}
              placeholder="(each mailbox’s own name)"
            />
          </Field>
        </div>
      </div>

      {/* ── Sequence ────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="card-title" style={{ marginBottom: 4 }}>Sequence</h3>
        <p className="page-subtitle" style={{ marginBottom: 16 }}>
          Step 1 is the first email. Later steps only send if the contact has not replied, and thread
          under the original as a reply.
        </p>

        {draft.steps.map((step, i) => (
          <div key={i} className={styles.step}>
            <div className={styles.stepHeader}>
              <div className={styles.stepNumber}>{i + 1}</div>
              <div className={styles.stepTitle}>
                {i === 0 ? 'First email' : `Follow-up ${i}`}
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => runPreview(i)}
                title="Preview this step"
                disabled={previewLoading}
              >
                {previewLoading && previewStep === i
                  ? <span className="spinner" style={{ width: 14, height: 14 }} />
                  : <Eye size={14} />}
              </button>
              {draft.steps.length > 1 && (
                <button className="btn btn-ghost btn-sm" onClick={() => removeStep(i)} title="Remove step">
                  <Trash2 size={14} />
                </button>
              )}
            </div>

            {i > 0 && (
              <div className={styles.stepWait}>
                <Clock size={13} /> Send
                <input
                  className="input"
                  type="number"
                  min="0"
                  max="90"
                  value={step.waitDays}
                  onChange={(e) => patchStep(i, { waitDays: Number(e.target.value) })}
                  style={{ width: 70, padding: '4px 8px' }}
                />
                days after the previous step, if there is no reply
              </div>
            )}

            <div className={styles.stepBody}>
              <Field
                label="Subject"
                help={i > 0 ? 'Leave blank to reply in the original thread as "Re: …" — recommended for follow-ups.' : undefined}
              >
                <input
                  className="input"
                  value={step.subject}
                  onChange={(e) => patchStep(i, { subject: e.target.value })}
                  placeholder={i === 0 ? 'Quick question about {{company}}' : '(reply in the same thread)'}
                />
              </Field>

              <div className={styles.formatChoice}>
                <div className={styles.segmented}>
                  <button
                    type="button"
                    className={`${styles.segment} ${(step.bodyFormat || 'text') === 'text' ? styles.segmentActive : ''}`}
                    onClick={() => patchStep(i, { bodyFormat: 'text' })}
                  >
                    <Type size={13} /> Plain text
                  </button>
                  <button
                    type="button"
                    className={`${styles.segment} ${step.bodyFormat === 'html' ? styles.segmentActive : ''}`}
                    onClick={() => patchStep(i, { bodyFormat: 'html' })}
                  >
                    <Code size={13} /> HTML
                  </button>
                </div>
                <span className={styles.formatHelp}>
                  {step.bodyFormat === 'html'
                    ? 'Designed email. Right for announcements to people who already know you; costly for cold outreach, where a plain message from a person outperforms a designed one.'
                    : 'Sent as a person would type it — no CSS, no images. The right default for cold outreach.'}
                </span>
              </div>

              {step.bodyFormat === 'html' ? (
                <HtmlBodyEditor
                  jwtToken={jwtToken}
                  value={step.body}
                  onChange={(html) => patchStep(i, { body: html })}
                  coldOutreach
                />
              ) : (
              <Field label="Body">
                <textarea
                  className={`input ${styles.textarea}`}
                  value={step.body}
                  onChange={(e) => patchStep(i, { body: e.target.value })}
                  placeholder={'Hi {{firstName|there}},\n\nI came across {{company}} and…\n\nWorth a quick chat?'}
                />
              </Field>
              )}

              <div className={styles.mergeHint}>
                <strong>Merge tags:</strong> <code>{'{{firstName}}'}</code> <code>{'{{lastName}}'}</code>{' '}
                <code>{'{{company}}'}</code> <code>{'{{title}}'}</code> — plus any column from your CSV.
                Add a fallback with <code>{'{{firstName|there}}'}</code>.
                <br />
                <strong>Spintax:</strong> <code>{'[[Hi|Hey|Hello]]'}</code> picks one variant per
                recipient, so 400 emails are not byte-identical — which is itself a spam signal.
              </div>
            </div>
          </div>
        ))}

        <button className="btn btn-ghost" onClick={addStep}>
          <Plus size={16} /> Add a follow-up
        </button>
      </div>

      {/* ── Preview ─────────────────────────────────────────────────────── */}
      {preview && (
        <div ref={previewRef} className="card" style={{ marginBottom: 20 }}>
          <h3 className="card-title" style={{ marginBottom: 12 }}>
            Preview — step {previewStep + 1}, as {preview.contact.email} would receive it
          </h3>

          {preview.missingTags?.length > 0 && (
            <div className={styles.warnBox}>
              <div className={styles.boxTitle}><AlertTriangle size={16} /> Unfillable merge tags</div>
              This contact has no value for {preview.missingTags.map((t) => <code key={t}>{`{{${t}}}`}</code>)}.
              They would render blank. Give them a fallback like <code>{'{{firstName|there}}'}</code> —
              an email opening with "Hi ," is the clearest possible sign of an automated blast.
            </div>
          )}

          {preview.imageProblems?.length > 0 && (
            <div className={styles.dangerBox}>
              <div className={styles.boxTitle}><AlertTriangle size={16} /> These images will break for recipients</div>
              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {preview.imageProblems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </div>
          )}

          {preview.advice?.length > 0 && (
            <div className={styles.warnBox}>
              <div className={styles.boxTitle}><AlertTriangle size={16} /> Worth knowing before you send</div>
              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {preview.advice.map((a) => <li key={a.text}>{a.text}</li>)}
              </ul>
            </div>
          )}

          <div className={styles.preview}>
            <div className={styles.previewHeader}>
              {preview.from && (
                <div style={{ marginBottom: 4 }}>
                  <strong>From:</strong>{' '}
                  {preview.from.name
                    ? `${preview.from.name} <${preview.from.address}>`
                    : preview.from.address}
                </div>
              )}
              <strong>Subject:</strong> {preview.subject}
            </div>
            {/* Rendered in a sandboxed frame rather than inline. Built server-side
                by the same code the sender uses, so this is what goes on the wire —
                and a pasted template's own <style> block cannot escape to repaint
                the admin UI around it. */}
            <iframe
              title="Message preview"
              className={styles.previewFrame}
              sandbox=""
              srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:16px;font-family:-apple-system,Segoe UI,Roboto,sans-serif;font-size:14px;line-height:1.6;color:#111;background:#fff}img{max-width:100%}a{color:#2563eb}</style></head><body>${preview.html}</body></html>`}
            />
          </div>

        </div>
      )}

      {/* ── Test send ───────────────────────────────────────────────────── */}
      {/* Its own card rather than tucked inside the preview: sending yourself a
          real message is the only check that shows how the mail actually renders
          and which folder it lands in, so it should not be reachable only by
          first opening a preview. */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="card-title" style={{ marginBottom: 4 }}>Send yourself a test</h3>
        <p className="page-subtitle" style={{ marginBottom: 16 }}>
          The only check that tells you how this renders in a real client, and whether it reaches
          the inbox. Worth doing before every launch.
        </p>

        <div className={styles.toolbar} style={{ marginBottom: 0 }}>
          <select
            className="input"
            style={{ width: 'auto' }}
            value={previewStep}
            onChange={(e) => setPreviewStep(Number(e.target.value))}
          >
            {draft.steps.map((_, i) => (
              <option key={i} value={i}>{i === 0 ? 'First email' : `Follow-up ${i}`}</option>
            ))}
          </select>
          {allocations.length > 1 && (
            <select
              className="input"
              style={{ width: 'auto' }}
              value={testMailbox}
              onChange={(e) => setTestMailbox(e.target.value)}
              title="Each domain has to be tested separately"
            >
              <option value="">From {allocations[0]?.address}</option>
              {allocations.slice(1).map((a) => (
                <option key={a.mailbox} value={a.mailbox}>From {a.address}</option>
              ))}
            </select>
          )}
          <input
            className="input"
            style={{ maxWidth: 260 }}
            placeholder="your@email.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
          <ActionButton
            className="btn btn-primary"
            onClick={sendTest}
            icon={<Send size={16} />}
            busyLabel="Sending…"
            doneLabel="Sent"
            failLabel="Send failed"
          >
            Send a real test
          </ActionButton>
          <ActionButton
            className="btn btn-ghost"
            onClick={() => runPreview(previewStep)}
            icon={<Eye size={16} />}
            busyLabel="Building…"
            doneLabel="Shown below"
            failLabel="Preview failed"
          >
            Preview
          </ActionButton>
        </div>
        <p className="text-sm text-muted" style={{ marginTop: 12, marginBottom: 0 }}>
          Open it, then use “Show original” in Gmail and confirm the headers read{' '}
          <code>dkim=pass spf=pass dmarc=pass</code>. Note which folder it landed in.
          {allocations.length > 1 && (
            <> Test <strong>every</strong> domain separately — authentication and inbox placement
              are per-domain, so one passing test says nothing about the other {allocations.length - 1}.</>
          )}
        </p>
      </div>

      {/* ── Audience ────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="card-title" style={{ marginBottom: 16 }}>Audience</h3>
        <div className={styles.toolbar}>
          <select className="input" style={{ width: 'auto' }} value={enrollList} onChange={(e) => setEnrollList(e.target.value)}>
            <option value="">Every mailable contact</option>
            {lists.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={excludeRoles} onChange={(e) => setExcludeRoles(e.target.checked)} />
            Exclude role accounts (info@, support@…)
          </label>
          <ActionButton
            className="btn btn-primary"
            onClick={enroll}
            icon={<Users size={16} />}
            busyLabel="Enrolling…"
            doneLabel="Enrolled"
            failLabel="Not enrolled"
          >
            Enroll contacts
          </ActionButton>
        </div>
        <p className="text-sm text-muted" style={{ margin: 0, lineHeight: 1.6 }}>
          Bounced and unsubscribed addresses stay suppressed. Previous replies are listed for review,
          but remain eligible unless you choose to disqualify them from this campaign.
        </p>

        <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <strong>{audience.summary?.total || 0} contacts imported</strong>
          </div>
          <div className={styles.audienceButtons}>
            <button type="button" className="btn btn-primary" onClick={() => openContactList('all')}>
              View full contact list
            </button>
            <button type="button" className="btn btn-primary" onClick={() => openContactList('replied')}>
              View replied contacts ({audience.summary?.replied || 0})
            </button>
          </div>
        </div>
      </div>

      {/* ── Throttling ──────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="card-title" style={{ marginBottom: 4 }}>Sending schedule</h3>
        <p className="page-subtitle" style={{ marginBottom: 16 }}>
          These settings are the difference between outreach and a spam blast.
        </p>

        <div className={styles.fieldRow} style={{ marginBottom: 16 }}>
          <Field label="Timezone" help="Should match where your recipients work.">
            <select className="input" value={draft.schedule.timezone}
              onChange={(e) => patchSchedule({ timezone: e.target.value })}>
              {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>

        <p className="page-subtitle" style={{ marginBottom: 16 }}>
          Emails are spread across all 24 hours of each enabled sending day.
        </p>

        <Field label="Sending days" help="Weekend cold email sits unread until Monday, then gets bulk-deleted — which reads as disengagement.">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DAY_LABELS.map((label, day) => {
              const on = (draft.schedule.daysOfWeek || []).includes(day);
              return (
                <button
                  key={label}
                  className={on ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                  onClick={() => toggleDay(day)}
                  type="button"
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Field>

        <div className={styles.infoBox} style={{ marginTop: 16 }}>
          <div className={styles.boxTitle}><Clock size={16} /> Volume and spacing live on the mailbox</div>
          How many a day, and how far apart, are set per mailbox on the Mailboxes tab — because those
          are properties of an address, not of a campaign. Two campaigns sharing one address have to
          interleave into a single rhythm, or the address sends double what either of them thinks it
          is sending. What this campaign takes from each mailbox is set under “Sending from” above.
        </div>

        <div style={{ marginTop: 8 }}>
          <Toggle
            label="Ramp up volume gradually"
            help={`Starts at ${draft.rampUp.startCap}/day and adds ${draft.rampUp.incrementPerDay}/day until this campaign reaches its ${perDay}/day. Spread proportionally across its mailboxes, so the domains stay balanced while it climbs. Separate from a mailbox's own warm-up — the lower of the two wins.`}
            checked={draft.rampUp.enabled}
            onChange={(v) => setDraft((d) => ({ ...d, rampUp: { ...d.rampUp, enabled: v } }))}
          />
          <Toggle
            label="Stop the sequence when someone replies"
            help="Leave this on. Continuing to send follow-ups after a reply is the fastest route to a spam complaint."
            checked={draft.stopOnReply}
            onChange={(v) => setDraft((d) => ({ ...d, stopOnReply: v }))}
          />
          <Toggle
            label="Include an unsubscribe option"
            help="Adds a one-click List-Unsubscribe header and a plain opt-out line. Gmail and Yahoo require this from bulk senders — without it, the only way out is the Report Spam button."
            checked={draft.includeUnsubscribe}
            onChange={(v) => setDraft((d) => ({ ...d, includeUnsubscribe: v }))}
          />
        </div>
      </div>

      {/* ── Recent activity ─────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="card-title" style={{ marginBottom: 16 }}>Recent sends</h3>
        {activity.length === 0 ? (
          <Empty icon={<Send size={36} color="var(--text-muted)" />} title="Nothing sent yet">
            Once the campaign starts, every message appears here.
          </Empty>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Sent</th><th>To</th><th>Step</th><th>Subject</th><th>Status</th></tr>
              </thead>
              <tbody>
                {activity.map((m) => (
                  <tr key={m._id}>
                    <td className={styles.cellMuted}>{formatDateTime(m.sentAt)}</td>
                    <td>{m.contact?.email || m.toAddress}</td>
                    <td className={styles.cellMuted}>{m.stepIndex < 0 ? 'Reply' : m.stepIndex + 1}</td>
                    <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.subject}</td>
                    <td><StatusBadge status={m.status === 'sent' ? 'completed' : 'failed'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {contactListOpen && (
        <ModalShell
          title={contactListMode === 'replied' ? 'Replied contacts' : 'Full contact list'}
          subtitle={`${contactList.total || 0} matching contacts in the selected source`}
          eyebrow="Audience"
          icon={Users}
          size="lg"
          className={styles.audienceModal}
          onClose={() => setContactListOpen(false)}
        >
          <form onSubmit={searchContactList} className={styles.toolbar} style={{ marginBottom: 14 }}>
            <input
              className="input"
              value={contactSearchInput}
              onChange={(event) => setContactSearchInput(event.target.value)}
              placeholder="Search all names, emails, phones or companies"
            />
            <button type="submit" className="btn btn-primary">Search</button>
          </form>

          <div className={`${styles.tableWrap} ${styles.audienceTableWrap}`} style={{ maxHeight: '56vh', overflowY: 'auto' }}>
            <table className={`${styles.table} ${styles.audienceTable}`}>
              <thead><tr><th /><th>Email</th><th>Name</th><th>Phone</th><th>Status</th></tr></thead>
              <tbody>
                {(contactList.contacts || []).map((contact) => (
                  <tr key={contact._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedContacts.includes(contact._id)}
                        disabled={contact.manuallyExcluded || contact.unavailable}
                        onChange={() => toggleAudienceContact(contact._id)}
                        aria-label={`Select ${contact.email}`}
                      />
                    </td>
                    <td>{contact.email}</td>
                    <td>{[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—'}</td>
                    <td>{contact.phone || '—'}</td>
                    <td><StatusBadge status={contact.manuallyExcluded ? 'stopped' : contact.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {contactList.pages > 1 && (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center', marginTop: 14 }}>
              <button type="button" className="btn btn-ghost btn-sm" disabled={contactListPage <= 1} onClick={() => setContactListPage((page) => page - 1)}>Previous</button>
              <span className="text-sm text-muted">Page {contactListPage} of {contactList.pages}</span>
              <button type="button" className="btn btn-ghost btn-sm" disabled={contactListPage >= contactList.pages} onClick={() => setContactListPage((page) => page + 1)}>Next</button>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={() => setContactListOpen(false)}>Close</button>
            <button type="button" className="btn btn-primary" disabled={!selectedContacts.length} onClick={disqualifySelected}>
              Disqualify selected{selectedContacts.length ? ` (${selectedContacts.length})` : ''}
            </button>
          </div>
        </ModalShell>
      )}

      <ActionButton
        className="btn btn-ghost"
        onClick={archive}
        icon={<Archive size={16} />}
        busyLabel="Archiving…"
        doneLabel="Archived"
        failLabel="Not archived"
        style={{ color: 'var(--danger)' }}
      >
        Archive this campaign
      </ActionButton>
    </div>
  );
};

export default CampaignEditor;
